import React, { useEffect } from "react";
import PopImage from "../components/PopImage";
import {
  Page,
  HeroWrap,
  Label,
  Headline,
  SubtitleRow,
  HeroImage,
  SectionHeader,
  ChallengeSection,
  MetricCard,
  ProcessSection,
  ProcessCol,
  SolutionGrid,
  ResultsSection
} from "../components/CaseStudyLayout";

import eyes from "../images/eyes.png";
import eyescanvid from "../images/eyescanvid.mov";
import eyescan7 from "../images/eyescan/eyescan7.png";
import eyescan8 from "../images/eyescan/eyescan8.png";
import eyescan10 from "../images/eyescan/eyescan10.png";
import eyescan11 from "../images/eyescan/eyescan11.png";
import eyescan12 from "../images/eyescan/eyescan12.png";

const Eyescann = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <HeroWrap>
        <Label>UI / FULL STACK ENGINEERING — MEDTECH</Label>
        <Headline>EyeScan</Headline>
        <SubtitleRow>
          <p>
            Ophthalmologists rely on labs to diagnose diseased eyes from photos, which takes days. 
            Leveraging advancements in AI, we built a proof-of-concept diagnostic application enabling doctors to capture images and receive near-instant readouts during the same appointment.
          </p>
          <div className="meta">
            <strong>ROLE</strong>
            <span>UI Designer & Developer</span>
            <strong>CONTEXT</strong>
            <span>Proof of Concept</span>
          </div>
        </SubtitleRow>
      </HeroWrap>

      <HeroImage>
        <PopImage src={eyes} alt="EyeScan AI Diagnostic Interface" title="EyeScan" />
      </HeroImage>

      <ChallengeSection>
        <div className="left">
          <h2>The Challenge</h2>
          <p>
            The interface needed to seamlessly integrate with complex medical hardware while remaining intuitive enough for rapid tablet usage in clinically dark environments.
          </p>
          <p>
            I had to establish a rigid design system to handle the dense optical data, whilst simultaneously architecting the real-time WebSocket backend to control the camera.
          </p>
        </div>
        <div className="right">
          <div className="metrics-box">
            <MetricCard>
              <h3>Hardware UX</h3>
              <span>NATIVE INTEGRATION</span>
              <p style={{fontSize: '0.85rem', opacity: 0.8, marginTop: '10px'}}>Required near-instant hardware feedback mapped to UI states.</p>
            </MetricCard>
            <MetricCard>
              <h3>Tech Stack</h3>
              <span>END-TO-END</span>
              <ul>
                <li>ReactJS (Frontend)</li>
                <li>WebSockets (Streaming)</li>
                <li>Figma (Prototyping)</li>
              </ul>
            </MetricCard>
          </div>
        </div>
      </ChallengeSection>

      <ProcessSection>
        <SectionHeader>
          <h2>The Process</h2>
          <span>PHASE 01 — 03</span>
        </SectionHeader>
        <div className="grid-3">
          <ProcessCol>
            <div className="icon">1</div>
            <h3>Backend Architecture</h3>
            <p>
              Initiated development by standing up backend APIs and a WebSocket connection, enabling latency-free control over the camera's optical and position settings directly from the UI.
            </p>
            <PopImage src={eyescan8} alt="Initial Specs" className="pop-image" title="EyeScan" />
          </ProcessCol>
          
          <ProcessCol>
            <div className="icon">2</div>
            <h3>Design Systems Refinement</h3>
            <p>
              Analyzed the baseline UX wireframes and completely overhauled the color language, typography sizing, and touch-targets mapping to dark-room clinic realities.
            </p>
            <PopImage src={eyescan7} alt="Color Systems" className="pop-image" title="EyeScan" />
          </ProcessCol>

          <ProcessCol>
            <div className="icon">3</div>
            <h3>React Engineering</h3>
            <p>
              Coded the final responsive structure in React. After wiring the main data flow, I injected pixel-perfect styling, complex component animations, and micro-interactions for an elevated hardware feel.
            </p>
            <PopImage src={eyescan10} alt="React Layouts" className="pop-image" title="EyeScan" />
          </ProcessCol>
        </div>
      </ProcessSection>

      <SectionHeader style={{borderBottom: 'none'}}>
        <h2>Dynamic UI</h2>
        <span>INTERACTIVE PROTOTYPE</span>
      </SectionHeader>
      <SolutionGrid>
        <div className="wide pop-image">
           <PopImage src={eyescanvid} isVideo={true} alt="EyeScan Video Demo" title="EyeScan" />
        </div>
        <div className="pop-image">
          <PopImage src={eyescan11} alt="Diagnostic View" title="EyeScan" />
        </div>
        <div className="pop-image">
          <PopImage src={eyescan12} alt="Settings Interface" title="EyeScan" />
        </div>
      </SolutionGrid>

      <ResultsSection>
        <div className="left">
          <h2>Results & Metrics</h2>
          <p>
            We successfully delivered an incredibly rapid prototype in just 6 weeks. The robust connection between the hardware and the React UI demonstrated the massive potential of AI streamlining clinical workflows.
          </p>
        </div>
        <div className="metrics-grid">
          <div className="result-stat">
            <h3>84%</h3>
            <h4>Success Rate</h4>
            <p>During diagnostic tasks testing with 5 Ophthalmologists.</p>
          </div>
          <div className="result-stat">
            <h3>6 WKS</h3>
            <h4>Delivery Time</h4>
            <p>Rapid dual-track design & engineering pipeline.</p>
          </div>
        </div>
      </ResultsSection>

    </Page>
  );
};

export default Eyescann;
